import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

interface Componente {
  numero: string;
  titulo: string;
  subtitulo: string;
  items: string[];
  resultado: string;
}

const componentes: Componente[] = [
  {
    numero: "A",
    titulo: "Riel A · Plataforma Estratégica de Marca Personal",
    subtitulo: "Dirigido por JP Barrios",
    items: [
      "Escucha: inmersión y relevamiento. Entrevistas con vos, con Tamara y con el equipo. Diagnóstico del punto de partida y de las brechas entre lo que sos y lo que el mercado percibe.",
      "Arqueología: identidad profunda. Tu historia, tu mirada sobre la pyme, tus convicciones. Lo que solo vos podés decir sobre la calle y que por eso nadie te copia.",
      "Estrategia: territorio, cliente ideal, propuesta de valor, diferencial y esencia. Con análisis del terreno: quién habla de la Macro, quién roza la micro, y dónde está el espacio libre para vos.",
      "Arquitectura: cómo se relacionan tu figura, Mercado Cheque y GWINN. Con Tamara en la mesa, porque como CMO su lectura condiciona la comunicación de las unidades.",
    ],
    resultado:
      "Una posición clara, propia y defendible, la base sobre la que todo lo demás se construye rápido y sin volver a definir puntos.\u00A0",
  },
  {
    numero: "B",
    titulo: "Riel B · CEO Visible",
    subtitulo: "Dirigido por Rita Galmarini",
    items: [
      "Liderazgo y presencia: cómo se para y se muestra un líder al que el mercado escucha.",
      "Expresión y corporalidad: el trabajo mental y corporal de comunicar sin bloqueo; que el cuerpo acompañe lo que la marca dice.",
      "Expresión y Presencia del Speaker:\u00A0 cómo esa presencia se traduce al escenario: cámaras empresarias, exposiciones y grupos de empresarios.",
    ],
    resultado:
      "Un líder que sostiene su marca con seguridad, dentro y fuera del escenario.",
  },
  {
    numero: "+",
    titulo: "Piloto de Voz",
    subtitulo: "El carro andando desde el primer mes",
    items: [
      "Desde la semana 5, cuando la posición y el sistema de mensajes están definidos, empezás a publicar con criterio estratégico.",
      "No esperás a que el sistema visual completo esté terminado. Tu primer objetivo no son las métricas: es el hábito de continuidad.",
    ],
    resultado:
      "Movimiento real desde temprano, con velocidad y credibilidad al mismo tiempo.",
  },
];

const ChiappellaComponentes = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section
      id="propuesta"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, hsl(220 69% 2%), hsl(217 61% 6%))",
      }}
    >
      <div className="container px-4 max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span
            className="text-sm font-semibold tracking-[0.25em] uppercase mb-4 block"
            style={{ color: "#5BA3E0" }}
          >
            La propuesta · Fase 1
          </span>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
            Fundación de Autoridad
            <br />
            <span style={{ color: "#5BA3E0" }}>
              90 días. Dos rieles en paralelo.
            </span>
          </h2>
          <p className="text-base md:text-lg text-white/75 max-w-3xl mx-auto leading-relaxed">
            Al terminar esta fase tenés definido quién sos como marca, qué
            territorio ocupás, cómo se ordenan tu figura y tus empresas, qué le
            decís a cada audiencia — y ya estás publicando con criterio.
          </p>
        </motion.div>

        {/* Accordion */}
        <div className="space-y-4">
          {componentes.map((c, index) => {
            const isOpen = openIndex === index;
            return (
              <motion.div
                key={c.numero}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="rounded-2xl border overflow-hidden transition-colors"
                style={{
                  borderColor: isOpen
                    ? "rgba(91,163,224,0.45)"
                    : "rgba(255,255,255,0.08)",
                  background: isOpen
                    ? "linear-gradient(135deg, hsl(217 61% 10%), hsl(215 48% 16%))"
                    : "hsl(220 69% 4%)",
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full text-left px-6 md:px-8 py-6 flex items-start gap-5 md:gap-8"
                >
                  <span
                    className="font-display text-3xl md:text-5xl font-bold flex-shrink-0 leading-none w-8 md:w-12"
                    style={{
                      color: isOpen ? "#5BA3E0" : "rgba(255,255,255,0.35)",
                    }}
                  >
                    {c.numero}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-xl md:text-2xl font-semibold text-white leading-tight">
                      {c.titulo}
                    </h3>
                    <p className="text-sm md:text-base text-white/60 mt-1">
                      {c.subtitulo}
                    </p>
                  </div>
                  <span
                    className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                    style={{
                      background: isOpen ? "#5BA3E0" : "rgba(255,255,255,0.06)",
                      color: isOpen ? "#0a162a" : "#ffffff",
                    }}
                  >
                    {isOpen ? (
                      <Minus className="w-5 h-5" />
                    ) : (
                      <Plus className="w-5 h-5" />
                    )}
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 md:px-8 pb-8 md:pl-[calc(2rem+4rem)] md:pr-20">
                        <ul className="space-y-3 mb-6">
                          {c.items.map((item) => (
                            <li
                              key={item}
                              className="flex gap-3 text-sm md:text-base text-white/80 leading-relaxed"
                            >
                              <span
                                className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ background: "#5BA3E0" }}
                              />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                        <div
                          className="rounded-xl p-5 border-l-4"
                          style={{
                            borderColor: "#1D9E75",
                            background: "rgba(29,158,117,0.08)",
                          }}
                        >
                          <p
                            className="text-xs uppercase tracking-[0.2em] mb-2 font-semibold"
                            style={{ color: "#4FD1A5" }}
                          >
                            Resultado
                          </p>
                          <p className="text-sm md:text-base text-white/85 italic leading-relaxed">
                            {c.resultado}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ChiappellaComponentes;
